import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form } from "react-bootstrap";
import { Button, ButtonType } from "../../../Components/Button/Button";
import { Spinner } from "../../../Components/Spinner";
import { ButtonFrameworkVariant } from "../../../Components/ButtonFramework/ButtonFramework";
import { useSetError } from "../../../redux/modals";
import { MdArrowForwardIos, MdArrowBackIos } from "react-icons/md";
import { awaitJSON, MetaLambdaFetch } from "../../../utils/fetch-utils";
import * as R from "ramda";
import {
  DROPDOWN_BTN,
  DEFAULT_SEARCH_RESPONSE_PAGINATED,
  SEARCH_MENU,
  SEARCH_DELAY,
} from "../../MetaBuyingConstants";

interface GeoLocationMenuProps {
  placeholder: string;
  menuId: string;
  closeMenu: () => void;
  showMenu: boolean;
  applySelections: (value: Record<string, any>[]) => void;
  appliedSelections: Record<string, any>[];
  business_name: string;
}

export const GeoLocationMenu = ({
  placeholder,
  menuId,
  closeMenu,
  showMenu,
  applySelections,
  appliedSelections,
  business_name,
}: GeoLocationMenuProps): JSX.Element => {
  const setError = useSetError();

  const [searchQuery, setSearchQuery] = useState("");
  const [prevSearchQuery, setPrevSearchQuery] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [paging, setPaging] = useState<{ before?: string; after?: string }>({});
  const [searchResponse, setSearchResponse] = useState<{
    query: string;
    data: Record<string, any>[];
    paging?: Record<string, any>;
  }>(DEFAULT_SEARCH_RESPONSE_PAGINATED);

  const dropdownBtnId = [menuId, DROPDOWN_BTN].join("_");
  const searchMenuId = [menuId, SEARCH_MENU].join("_");

  useEffect(() => {
    setSelectedLocations(R.clone(appliedSelections));
  }, [showMenu, appliedSelections]);

  const searchFunction = useCallback(
    async (location: string, nextPage?: string, prevPage?: string) => {
      const res = await MetaLambdaFetch("/searchMetaLocations", {
        params: {
          location,
          business_name,
          nextPage,
          prevPage,
        },
      });
      const locations = await awaitJSON(res);
      return locations;
    },
    [business_name]
  );

  const querySearchFunction = useCallback(
    (query: string, nextPage?: string, prevPage?: string) => {
      setLoading(true);
      setTimeoutId(
        setTimeout(() => {
          (async () => {
            try {
              let { data, paging } = await searchFunction(query, nextPage, prevPage);
              setSearchResponse({ query, data, paging });
            } catch (e) {
              setError({
                message: e instanceof Error ? e.message : String(e),
                reportError: e,
              });
              setLoading(false);
            }
          })();
        }, SEARCH_DELAY)
      );
    },
    [searchFunction, setError]
  );

  useEffect(() => {
    const { query, data, paging } = searchResponse;
    if (searchQuery === query && searchQuery.length) {
      setData(data);
      let newPaging: { before?: string; after?: string } = {};
      if (paging) {
        const { cursors, next, previous } = paging;
        if (cursors && next) {
          newPaging.after = cursors.after;
        }
        if (cursors && previous) {
          newPaging.before = cursors.before;
        }
      }
      setPaging(newPaging);
      setSearchResponse(DEFAULT_SEARCH_RESPONSE_PAGINATED);
      setLoading(false);
    }
  }, [searchResponse, searchQuery]);

  useEffect(() => {
    if (searchQuery !== prevSearchQuery) {
      setPrevSearchQuery(searchQuery);
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      if (searchQuery.length) {
        querySearchFunction(searchQuery);
      } else {
        setData([]);
        setPaging({});
        setLoading(false);
      }
    }
  }, [searchQuery, prevSearchQuery, querySearchFunction, timeoutId]);

  useEffect(() => {
    if (!showMenu) {
      setSearchQuery("");
      setData([]);
      setPaging({});
      setLoading(false);
      setSearchResponse(DEFAULT_SEARCH_RESPONSE_PAGINATED);
    }
  }, [showMenu]);

  const selectedKeys: string[] = useMemo(() => selectedLocations.map(({ key }) => key), [
    selectedLocations,
  ]);

  const getFullLocation = (location: Record<string, any>): string => {
    const { name, region, country_name, type } = location;
    switch (type) {
      case "city":
        return `${name}, ${region}, ${country_name}`;
      case "region":
        return `${name}, ${country_name}`;
      default:
        return location.name;
    }
  };

  return (
    <div id={dropdownBtnId} className="metaBuyingSearchMenu menuToggle">
      <Button className="dropdown-toggle" type={ButtonType.OUTLINED}>
        {appliedSelections.length > 0 ? (
          appliedSelections.map((location, index) => (
            <div
              key={index}
              className="appliedSelection"
              onClick={() => {
                const newAppliedSelections = R.remove(index, 1, appliedSelections);
                applySelections(newAppliedSelections);
                setSelectedLocations(newAppliedSelections);
                closeMenu();
              }}
            >
              <span>{getFullLocation(location)}</span>
            </div>
          ))
        ) : (
          <span className="searchMenuPlaceholder">{placeholder}</span>
        )}
      </Button>
      {showMenu && (
        <div id={searchMenuId} className="searchMenu menuToggle">
          {" "}
          <div>
            <Form.Control
              size="sm"
              className="searchBar"
              placeholder="Search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div className="searchMenuRows">
              {loading ? (
                <Spinner size={50} />
              ) : (
                data.map((option, index) => (
                  <div key={index} className="searchMenuRow">
                    <Form.Check
                      className="searchMenuOption"
                      key={index}
                      type="checkbox"
                      label={
                        <div>
                          {option.name}
                          <span className="sublabel">
                            {(() => {
                              let geoData: string[] = [];
                              ["region", "country_name"].forEach(key => {
                                if (
                                  option[key] &&
                                  (option.type !== "country" || key !== "country_name")
                                ) {
                                  geoData.push(option[key]);
                                }
                              });
                              return geoData.join(", ");
                            })()}
                          </span>
                        </div>
                      }
                      checked={R.includes(option.key, selectedKeys)}
                      id={option.name}
                      onChange={() =>
                        setSelectedLocations(
                          R.includes(option.key, selectedKeys)
                            ? selectedLocations.filter(({ key }) => key !== option.key)
                            : [...selectedLocations, option]
                        )
                      }
                    />
                  </div>
                ))
              )}
            </div>
            {!loading && (paging.after || paging.before) && (
              <div className="searchMenuPaging">
                {paging.before && (
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.LEADING_ICON}
                    onClick={() => querySearchFunction(searchQuery, undefined, paging.before)}
                    icon={<MdArrowBackIos />}
                    size="sm"
                    className="prevPage"
                  >
                    Prev
                  </Button>
                )}
                {paging.after && (
                  <Button
                    type={ButtonType.OUTLINED}
                    variant={ButtonFrameworkVariant.TRAILING_ICON}
                    onClick={() => querySearchFunction(searchQuery, paging.after)}
                    icon={<MdArrowForwardIos />}
                    size="sm"
                    className="nextPage"
                  >
                    Next
                  </Button>
                )}
              </div>
            )}
            <div className="searchMenuControls">
              <Button
                type={ButtonType.OUTLINED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={closeMenu}
              >
                Cancel
              </Button>
              <Button
                disabled={loading || R.isEmpty(selectedKeys)}
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.NO_ICON}
                onClick={() => {
                  applySelections(selectedLocations);
                  closeMenu();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
